// Launch-form state + request mapping (simulations.md). The form state is
// string-typed for inputs and mapped to the generated BatchPreviewRequest /
// BatchCreate shapes in one place, so the debounced preview and the launch
// POST can never drift. Sweep axis domains are unsealed (core axis registry),
// so accent/language/background are honest free-token comma lists. Variable
// override sets keep raw field input in state and become typed JsonValue only
// at the request boundary, using the current declaration.
import type { components } from "../../api/schema";
import {
  declarationConflict,
  declarationConflictMessage,
  groupDeclarationsByKey,
} from "../../lib/variableCascade";
import { unicodeCaseFold } from "../../lib/unicodeCaseFold";
import { parseVariableValue } from "../variables/variableFormState";

type BatchCreate = components["schemas"]["BatchCreate"];
type BatchPreviewRequest = components["schemas"]["BatchPreviewRequest"];
type BatchVariableSweep = components["schemas"]["BatchVariableSweep"];
export type VariableDeclaration = components["schemas"]["VariableResponse"];

export interface VariableSweepDraft {
  id: string;
  values: Record<string, string>;
}

export interface SimulationLaunchState {
  agentVersionId: string;
  batchConcurrencyMax: string;
  connectionId: string;
  displayName: string;
  maxDurationSeconds: string;
  personaIds: string[];
  sampleEnabled: boolean;
  sampleSize: string;
  sampleSeed: string;
  scenarioSetId: string;
  sweepAccent: string;
  sweepBackground: string;
  sweepLanguage: string;
  sweepVariables: VariableSweepDraft[];
  templateId: string;
}

export interface LaunchRequirement {
  id: "version" | "connection" | "personas" | "scenario-set";
  label: string;
}

export function canonicalizeVariableSweepDrafts(
  drafts: VariableSweepDraft[],
  variables: VariableDeclaration[],
): VariableSweepDraft[] {
  const declarations = groupDeclarationsByKey(variables);
  return drafts.map((draft) => ({
    ...draft,
    values: Object.fromEntries(
      Object.entries(draft.values).map(([key, raw]) => {
        const canonicalKey =
          declarations.get(unicodeCaseFold(key))?.[0]?.key ?? key;
        return [canonicalKey, raw];
      }),
    ),
  }));
}

const LAUNCH_REQUIREMENTS: readonly (LaunchRequirement & {
  isMissing: (state: SimulationLaunchState) => boolean;
})[] = [
  {
    id: "version",
    isMissing: (state) => state.agentVersionId.length === 0,
    label: "Version",
  },
  {
    id: "connection",
    isMissing: (state) => state.connectionId.length === 0,
    label: "Connection",
  },
  {
    id: "personas",
    isMissing: (state) => state.personaIds.length === 0,
    label: "Personas",
  },
  {
    id: "scenario-set",
    isMissing: (state) => state.scenarioSetId.length === 0,
    label: "Scenario set",
  },
];

export function initialLaunchState(displayName: string): SimulationLaunchState {
  return {
    agentVersionId: "",
    batchConcurrencyMax: "",
    connectionId: "",
    displayName,
    maxDurationSeconds: "",
    personaIds: [],
    sampleEnabled: false,
    sampleSize: "",
    sampleSeed: "",
    scenarioSetId: "",
    sweepAccent: "",
    sweepBackground: "",
    sweepLanguage: "",
    sweepVariables: [],
    templateId: "",
  };
}

export function missingLaunchRequirements(
  state: SimulationLaunchState,
): LaunchRequirement[] {
  return LAUNCH_REQUIREMENTS.filter((requirement) =>
    requirement.isMissing(state),
  ).map(({ id, label }) => ({ id, label }));
}

export function parseTokenList(raw: string): string[] {
  return raw
    .split(",")
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
}

function sweepEnumValues(variable: VariableDeclaration): string[] | null {
  if (variable.current_revision.value_type !== "enum") return null;
  return (variable.current_revision.allowed_values ?? []).filter(
    (value): value is string => typeof value === "string",
  );
}

// Total by construction: SimulationLaunchForm calls parseVariableSweeps on
// every render, so an unparsable cell falls back to its raw string exactly as
// the declaration-conflict branch does. variableSweepErrors is the only
// reporter and blocks the launch.
function typedSweepValue(
  raw: string,
  variable: VariableDeclaration | undefined,
): components["schemas"]["JsonValue"] {
  if (variable === undefined) return raw;
  try {
    return parseVariableValue(
      variable.key,
      variable.current_revision.value_type,
      sweepEnumValues(variable),
      raw,
    );
  } catch {
    return raw;
  }
}

export function variableSweepErrors(
  drafts: VariableSweepDraft[],
  variables: VariableDeclaration[],
): string[] {
  const declarations = groupDeclarationsByKey(variables);
  const errors: string[] = [];
  for (const draft of drafts) {
    for (const [key, raw] of Object.entries(draft.values)) {
      const group = declarations.get(unicodeCaseFold(key));
      const variable = group?.[0];
      if (variable === undefined) {
        errors.push(`${key} is not a declared variable.`);
        continue;
      }
      if (group === undefined) continue;
      const conflict = declarationConflict(group);
      if (conflict !== "none") {
        // Cross-declaration value_type / allowed_values precedence is
        // unsettled. Block only a sweep that touches the disputed key.
        errors.push(
          declarationConflictMessage(variable.key, group.length, conflict),
        );
        continue;
      }
      if (raw.trim().length > 0) {
        try {
          parseVariableValue(
            variable.key,
            variable.current_revision.value_type,
            sweepEnumValues(variable),
            raw,
          );
        } catch (error) {
          errors.push(error instanceof Error ? error.message : String(error));
        }
      }
    }
  }
  return [...new Set(errors)];
}

export function parseVariableSweeps(
  drafts: VariableSweepDraft[],
  variables: VariableDeclaration[],
): BatchVariableSweep[] {
  const declarations = groupDeclarationsByKey(variables);
  return drafts.flatMap((draft) => {
    const values: Record<string, components["schemas"]["JsonValue"]> = {};
    for (const [key, raw] of Object.entries(draft.values)) {
      if (raw.trim().length === 0) {
        continue;
      }
      const group = declarations.get(unicodeCaseFold(key));
      const variable = group?.[0];
      if (group && declarationConflict(group) !== "none") {
        // Preserve authored input while the conflict error blocks use. The
        // declaration winner remains unruled, so do not coerce the value.
        values[variable?.key ?? key] = raw;
        continue;
      }
      values[variable?.key ?? key] = typedSweepValue(raw, variable);
    }
    return Object.keys(values).length > 0 ? [{ values }] : [];
  });
}

function parsedInteger(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^-?\d+$/u.test(trimmed)) {
    return null;
  }
  const value = Number(trimmed);
  return Number.isSafeInteger(value) ? value : null;
}

export function sampleSeedError(raw: string): string | undefined {
  if (raw.trim().length === 0) {
    return "Enter a seed or generate one.";
  }
  return parsedInteger(raw) === null ? "Enter a whole-number seed." : undefined;
}

// The preview/create pipeline needs every fixed axis picked before a request
// is well-formed (docs/14 §7 launch-time hard gate).
export function isPreviewable(
  state: SimulationLaunchState,
  variables: VariableDeclaration[] = [],
): boolean {
  if (missingLaunchRequirements(state).length > 0) {
    return false;
  }
  const sampleSize = parsedInteger(state.sampleSize);
  if (
    state.sampleEnabled &&
    (sampleSize === null ||
      sampleSize <= 0 ||
      sampleSeedError(state.sampleSeed) !== undefined)
  ) {
    return false;
  }
  return variableSweepErrors(state.sweepVariables, variables).length === 0;
}

// The seed the form stamps when sampling is toggled on with a blank seed —
// generated once and kept in state so the debounced preview body stays
// stable across renders (docs/14 §3.1 reproducibility).
export function randomSampleSeed(): number {
  return Math.floor(Math.random() * 1_000_000);
}

export function toPreviewRequest(
  state: SimulationLaunchState,
  variables: VariableDeclaration[] = [],
): BatchPreviewRequest {
  const sampleSize = parsedInteger(state.sampleSize);
  const sampleSeed = parsedInteger(state.sampleSeed);
  return {
    agent_version_id: state.agentVersionId,
    connection_id: state.connectionId,
    persona_ids: state.personaIds,
    sample:
      state.sampleEnabled && sampleSize !== null && sampleSeed !== null
        ? { sample_size: sampleSize, seed: sampleSeed, strategy: "random" }
        : null,
    scenario_set_id: state.scenarioSetId,
    simulation_options: {
      batch_concurrency_max: parsedInteger(state.batchConcurrencyMax),
      max_duration_seconds: parsedInteger(state.maxDurationSeconds),
    },
    sweep: {
      accent: parseTokenList(state.sweepAccent),
      background: parseTokenList(state.sweepBackground),
      language: parseTokenList(state.sweepLanguage),
      variables: parseVariableSweeps(state.sweepVariables, variables),
    },
    template_id: state.templateId.length > 0 ? state.templateId : null,
  };
}

// A Template IS a saved launch spec (docs/14 §5): the stored object is the
// `BatchPreviewRequest` body flattened at the top level, so running a
// Template instantiates a batch through the SAME `:preview` / Create path as
// an ad-hoc launch. Two keys are deliberately absent. `template_id` is launch
// provenance — a Template does not record where it itself came from, and
// storing one would let a saved spec point at another Template. `display_name`
// is the BATCH's name, chosen per run; the Template's own name is a column on
// the row, not a spec key.
//
// `launchStateFromSpec` reads every key this writes; the round trip is pinned
// by `simulationLaunchState.test.ts` and is the anti-drift net for the shape.
export function toTemplateSpec(
  state: SimulationLaunchState,
  variables: VariableDeclaration[] = [],
): Record<string, unknown> {
  const preview = toPreviewRequest(state, variables);
  const spec: Record<string, unknown> = {
    ...preview,
    sample: preview.sample ? { ...preview.sample, seed: null } : null,
  };
  delete spec.template_id;
  return spec;
}

// Saving a Template requires the four fixed axes — the empty `{}` spec that
// the old raw-JSON editor defaulted to could never launch anything. It does
// NOT require a clean `:preview`: a Template is authored ahead of the data it
// will run against, so preview findings are advisory here (they stay blocking
// on the launch form, where `isPreviewable` + a clean preview gate Launch).
export function isTemplateSaveable(state: SimulationLaunchState): boolean {
  return missingLaunchRequirements(state).length === 0;
}

export function toCreateRequest(
  state: SimulationLaunchState,
  variables: VariableDeclaration[] = [],
): BatchCreate {
  return {
    ...toPreviewRequest(state, variables),
    display_name: state.displayName.trim(),
  };
}

// Best-effort prefill from an authored batch spec (`{fixed, sweep, sample,
// simulation_options}`, the shape core writes to `batches.spec`) for
// duplicate-into-form. Unknown/missing keys keep the blank default.
export function launchStateFromSpec(
  spec: Record<string, unknown> | null,
  displayName: string,
  variables: VariableDeclaration[],
): SimulationLaunchState {
  const state = initialLaunchState(displayName);
  if (spec === null) {
    return state;
  }
  // Launched batches store fixed axes under `fixed`; saved Templates store
  // the BatchPreview/Create launch fields at the top level. Both feed this
  // one form so Duplicate and Run converge on the same preview/Create path.
  const storedFixed = asRecord(spec.fixed);
  const fixed = Object.keys(storedFixed).length > 0 ? storedFixed : spec;
  const sweep = asRecord(spec.sweep);
  const sample = asRecord(spec.sample);
  const options = asRecord(spec.simulation_options);
  state.agentVersionId = asString(fixed.agent_version_id);
  state.connectionId = asString(fixed.connection_id);
  state.scenarioSetId = asString(fixed.scenario_set_id);
  state.templateId = asString(fixed.template_id);
  state.personaIds = asStringArray(fixed.persona_ids);
  state.sweepLanguage = asStringArray(sweep.language).join(", ");
  state.sweepAccent = asStringArray(sweep.accent).join(", ");
  state.sweepBackground = asStringArray(sweep.background).join(", ");
  state.sweepVariables = asVariableDrafts(sweep.variables, variables);
  if (Object.keys(sample).length > 0) {
    state.sampleEnabled = true;
    state.sampleSize = asNumberString(sample.sample_size);
    // Pin a fresh seed in form state for a stable manual preview and launch.
    state.sampleSeed =
      sample.seed === null
        ? String(randomSampleSeed())
        : asNumberString(sample.seed);
  }
  state.maxDurationSeconds = asNumberString(options.max_duration_seconds);
  state.batchConcurrencyMax = asNumberString(options.batch_concurrency_max);
  return state;
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function asNumberString(value: unknown): string {
  return typeof value === "number" ? String(value) : "";
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function asVariableDrafts(
  value: unknown,
  variables: VariableDeclaration[],
): VariableSweepDraft[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const drafts: VariableSweepDraft[] = [];
  for (const [index, item] of value.entries()) {
    const values = asRecord(asRecord(item).values);
    const rawValues = Object.fromEntries(
      Object.entries(values)
        .filter((entry): entry is [string, string | number | boolean] =>
          ["string", "number", "boolean"].includes(typeof entry[1]),
        )
        .map(([key, itemValue]) => [key, String(itemValue)]),
    );
    if (Object.keys(rawValues).length > 0) {
      drafts.push({ id: `prefill-${String(index + 1)}`, values: rawValues });
    }
  }
  return canonicalizeVariableSweepDrafts(drafts, variables);
}
